"use client"

import clsx from "clsx";
import { useState, useEffect, useRef, useCallback } from "react";
import { postFormData } from "@/actions/fetch-action";
import Cta from "@/components/cta/cta";
import Change from "@/components/icon/change";
import Close from "@/components/icon/close";
import Plus from "@/components/icon/plus";
import SubmitStorySuccess from "@/components/stories/submit-story-success";
import { STORIES_BASE_PATH } from "@/constants/routes";
import StoryFormOverlay from "./story-form-overlay";

function StoryFormLayout() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [story, setStory] = useState('');
  const [photo, setPhoto] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false)

  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const scrollVal = useRef(0);

  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

  const validatePhoto = async (file) => {
    if (!file) {
      setErrors(prev => { const next = { ...prev }; delete next.photo; return next; });
      return;
    }

    const photoErrors = [];

    // File format: JPEG, PNG, or WebP
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      photoErrors.push('File format - JPEG, PNG, or WebP');
    }

    // File size: up to 10MB
    if (file.size > 10 * 1024 * 1024) {
      photoErrors.push('File size - up to 10MB');
    }

    // Image dimensions: at least 1040 × 720px
    const dimensions = await new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });

    if (dimensions && (dimensions.width < 1040 || dimensions.height < 720)) {
      photoErrors.push('Image dimensions - at least 1040px wide and 720px tall');
    }

    // Resolution: minimum 72ppi (from file metadata)
    const ppi = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const view = new DataView(evt.target.result);

          if (file.type === 'image/jpeg') {
            // JFIF APP0 segment: FFD8 SOI, FFE0 APP0, then JFIF\0, version, units, xDensity
            if (view.getUint16(0) === 0xFFD8 && view.getUint16(2) === 0xFFE0) {
              const units = view.getUint8(13); // 1 = dpi, 2 = dpcm
              if (units === 1) {
                resolve(view.getUint16(14));
              } else if (units === 2) {
                resolve(Math.round(view.getUint16(14) * 2.54));
              } else {
                resolve(null);
              }
            } else {
              resolve(null);
            }
          } else if (file.type === 'image/png') {
            // Scan chunks for pHYs (pixels per unit X/Y + unit specifier)
            let offset = 8; // Skip 8-byte PNG signature
            while (offset + 12 <= evt.target.result.byteLength) {
              const len = view.getUint32(offset);
              const type = view.getUint32(offset + 4);
              if (type === 0x70485973) { // 'pHYs'
                const unit = view.getUint8(offset + 16); // 1 = meter
                if (unit === 1) {
                  resolve(Math.round(view.getUint32(offset + 8) / 39.3701));
                } else {
                  resolve(null);
                }
                return;
              }
              offset += 12 + len;
            }
            resolve(null);
          } else {
            resolve(null);
          }
        } catch {
          resolve(null);
        }
      };
      reader.onerror = () => resolve(null);
      reader.readAsArrayBuffer(file.slice(0, 65536)); // First 64KB covers all metadata
    });

    if (ppi !== null && ppi < 72) {
      photoErrors.push('Resolution - minimum 72ppi');
    }

    setErrors(prev => {
      const next = { ...prev };
      if (photoErrors.length > 0) {
        next.photo = photoErrors;
      } else {
        delete next.photo;
      }
      return next;
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;

    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      setPhoto(file);
      validatePhoto(file);
    }
  };
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPhotoValid = photo !== null;
  const isFormValid = name.trim() !== '' && isEmailValid && title.trim() !== '' && story.trim() !== '' && isPhotoValid && !errors.photo && isChecked;

  const setFieldError = (field, message) => {
    setErrors(prev => {
      const next = { ...prev };
      if (message) {
        next[field] = message;
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const handleNameBlur = () => {
    setFieldError('name', name.trim() === '' ? 'Please include your name' : '');
  };

  const handleEmailBlur = () => {
    if (email.trim() === '') {
      setFieldError('email', 'Invalid email');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFieldError('email', 'Invalid email');
    } else {
      setFieldError('email', '');
    }
  };

  const handleTitleBlur = () => {
    setFieldError('title', title.trim() === '' ? 'Please include a title' : '');
  };

  const handleStoryBlur = () => {
    setFieldError('story', story.trim() === '' ? 'Please include a story' : '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    const formData = new FormData(e.target);

    formData.append('name', name);
    formData.append('userEmail', email);
    formData.append('storyTitle', title);
    formData.append('storyContent', story);
    formData.append('image', photo, photo.name);

    // console.log(photo)

    // for (var pair of formData.entries()) {
    //   console.log(pair[0]+ ', ' + pair[1]); 
    // }

    try {
      const {res} = await postFormData(formData);
      if (res.success) {
        setIsSubmitted(true);
      } else {
        setErrors({submit: 'There was an error submitting your story, please try agian later.'});
      }

      // if (!res.ok && data.errors) {
      //   setErrors(data.errors);
      //   return;
      // }

    } catch (error) {
      setErrors({submit: 'There was an error submitting your story, please try agian later.'});
    }
  };

  const closeButtonRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
      case 'Escape':
        closeButtonRef.current?.click();
        break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const onPictureChange = (e) => { 
    const file = e.target.files?.[0] || null; 
    if (!file) {
      return
    }

    setPhoto(file); 
    validatePhoto(file); 
  }

  const checkBoxRef = useRef(null);

  const onAgree = useCallback(() => {
    setIsOverlayOpen(false);

    if (!isChecked) {
      checkBoxRef.current.click()
    }
  }, [isChecked]);

  const handleCheckBoxChecked = (e) => {
    setIsChecked(e.currentTarget.checked)
  }

  const openOverlay = () => {
    scrollVal.current = window.scrollY;
    setIsOverlayOpen(true)
  }

  useEffect(() => {
    if (!isOverlayOpen && scrollVal.current) {
      window.scrollTo(0, scrollVal.current)
    }
  }, [isOverlayOpen])

  return (
    <div
      data-story-theme={isSubmitted ? "main-dark" : "main-light"}
      className={clsx(
        "w-screen min-h-full-height-screen",
        isOverlayOpen ? 'full-height-screen overflow-y-hidden' : 'min-h-full-height-screen' 
      )}
    >
      <div
        className={clsx("fixed inset-0 z-0 t-colors", isSubmitted ? "bg-main-dark" : "bg-main-light")}
        aria-hidden="true"
      />
      <Cta
        inert={isOverlayOpen}
        ref={closeButtonRef}
        className={clsx(
          'absolute z-50 top-[calc(2.5rem)] right-6 cta-icon-stroke-theme cta-circle-small pointer-events-auto lg:fixed lg:top-[calc(var(--space-8))] lg:right-(--space-8) lg:cta-circle transition-[opacity,background-color,color] duration-500 delay-500',
          isSubmitted ? 'cta-light' : 'cta-dark',
        )}
        aria-label="Close"
        transitionName="overlay-out"
        href={`${STORIES_BASE_PATH}/250-stories`}
      >
        <Close className="w-(--space-7) h-auto" aria-hidden="true" />
      </Cta>

      {showSuccess ? (
        <div
          inert={isOverlayOpen}
          className="animate-fade-in min-h-full-height-screen relative flex max-lg:pt-(--header-height) lg:items-center justify-center"
          style={{ animation: 'fade-in-up 0.6s var(--quart-out) both' }}
        >
          <SubmitStorySuccess image={URL.createObjectURL(photo)} />
        </div>
      ) : (
        <div
          className={clsx('relative transition-opacity duration-500 flex flex-col justify-center py-[calc(4.6875rem)]', isSubmitted && 'opacity-0')}
          onTransitionEnd={() => { if (isSubmitted) setShowSuccess(true); }}
          inert={isOverlayOpen}
        >
          <div className="grid-9">
            <div className="col-span-full mb-6 lg:col-span-4 lg:col-span-4 lg:mb-14">
              <h1 className="heading-3-small text-pretty w-57.5 lg:heading-3 sm:w-full">Submit a photo or story</h1>
              <p className="mt-4 body-1-small text-pretty sm:w-110 lg:!w-full lg:body-2">How is your family part of American history? Who made a lasting mark on the country—or in your memory? Add your story to the Stories of US.</p>
            </div>
          </div>

          <form className="grid-9" onSubmit={handleSubmit}>
            <div className="col-span-full max-lg:order-2 max-lg:mt-6 flex flex-col gap-y-5 lg:col-span-6 lg:max-w-[94.87%]">
              <div className="flex flex-col gap-5 lg:flex-row">
                <div className="flex flex-col gap-y-3 flex-1">
                  <div className="flex items-center justify-between">
                    <label className="body-2-sans" htmlFor="name">Your name</label>
                    {errors.name && <span className="text-form-error-small text-ui-red lg:text-form-error">{errors.name}</span>}
                  </div>
                  <div className="input-focus-state">
                    <input className={clsx("text-form-input-small lg:text-form-input px-6 w-full h-13.5 bg-none bg-white rounded-[6px]", errors.name && "border border-ui-red")} type="text" id="name" required value={name} onChange={(e) => setName(e.target.value)} onBlur={handleNameBlur} />
                  </div>
                </div>

                <div className="flex flex-col gap-y-3 flex-1">
                  <div className="flex items-center justify-between">
                    <label className="body-2-sans" htmlFor="email">Your email address</label>
                    {errors.email && <span className="text-form-error-small text-ui-red lg:text-form-error">{errors.email}</span>}
                  </div>
                  <div className="input-focus-state">
                    <input className={clsx("text-form-input-small lg:text-form-input px-6 w-full h-13.5 bg-none bg-white rounded-[6px]", errors.email && "border border-ui-red")} type="email" id="email" required value={email} onChange={(e) => setEmail(e.target.value)} onBlur={handleEmailBlur} />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-y-3">
                <div className="flex items-center justify-between">
                  <label className="body-2-sans" htmlFor="title">Title</label>
                  {errors.title && <span className="text-form-error-small text-ui-red lg:text-form-error">{errors.title}</span>}
                </div>
                <div className="relative input-focus-state">
                  <input className={clsx("text-form-input-small lg:text-form-input pl-6 pr-17 w-full h-13.5 bg-none bg-white rounded-[6px]", errors.title && "border border-ui-red")} type="text" id="title" maxLength={50} required value={title} onChange={(e) => setTitle(e.target.value)} onBlur={handleTitleBlur} />
                  <span className={clsx("absolute top-1/2 -translate-y-1/2 right-6 body-3-sans-small", errors.title ? "text-ui-red" : "text-main-dark-600")}>{title.length} / 50</span>
                </div>
              </div>

              <div className="relative flex flex-col gap-y-3">
                <div className="flex items-center justify-between">
                  <label className="body-2-sans" htmlFor="story">Your story</label>
                  {errors.story && <span className="text-form-error-small text-ui-red lg:text-form-error">{errors.story}</span>}
                </div>
                <div className="input-focus-state">
                  <textarea className={clsx("text-form-input-small lg:text-form-input px-6 pt-4 pb-10 w-full h-48 bg-none bg-white rounded-[6px]", errors.story && "border border-ui-red")} id="story" maxLength={300} required value={story} onChange={(e) => setStory(e.target.value)} onBlur={handleStoryBlur} />
                </div>
                <span className={clsx("absolute right-6 bottom-4 body-3-sans-small", errors.story ? "text-ui-red" : "text-main-dark-600")}>{story.length} / 300</span>
              </div>

              <div className="flex flex-col gap-y-3">
                <label className="body-2-sans" htmlFor="location">Location of picture (optional)</label>
                <div className="input-focus-state">
                  <input className="text-form-input-small lg:text-form-input px-6 w-full h-13.5 bg-none bg-white rounded-[6px]" type="text" id="location" />
                </div>
              </div>

              <div className="flex items-start gap-x-3">
                <input type="checkbox" id="newsletter" className="peer sr-only" ref={checkBoxRef} onChange={handleCheckBoxChecked}/>
                <label htmlFor="newsletter" className="relative shrink-0 flex items-center justify-center w-6 h-6 bg-main-dark/20 rounded-[6px] cursor-pointer transition-[background-color] duration-200 peer-checked:bg-none bg-white peer-checked:[&>span]:opacity-100 peer-checked:[&>span]:scale-100 input-peer-focus-state">
                  <span className="w-2.5 h-2.5 rounded-full bg-main-dark opacity-0 scale-0 transition-[opacity,transform] duration-200" />
                </label>
                <label className="body-2-sans-small opacity-80 lg:body-2-sans cursor-pointer lg:max-w-[75%]" htmlFor="newsletter">
                  By opting in, you agree to receive communications from Ancestry via email. Stay updated with the latest news and offers. <button className="text-ui-green cursor-pointer" type="button" onClick={openOverlay}>Learn more</button>
                </label>
              </div>

              <Cta className="cta-big sm:w-fit" disabled={!isFormValid}>Submit your story</Cta>
              {errors.submit && <span className="text-form-error-small text-ui-red lg:text-form-error">{errors.submit}</span>}
            </div>

            <div className="col-span-full max-lg:order-1 lg:col-span-3 flex flex-col gap-y-3">
              <div
                className={clsx("relative p-3 rounded-[12px] border input-focus-state transition-[border-color] duration-200", errors.photo ? "border-ui-red" : isDragging ? "border-main-dark" : "border-[#38362D]/30", "aspect-[390/435]")}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className={clsx("border border-dashed w-full h-full rounded-[6px] cursor-pointer transition-[border-color] duration-200 aspect-[390/435]", isDragging ? "border-main-dark" : "border-[#38362D]/30", photo ? "relative" : "relative")}>
                  <label htmlFor="photo" className={clsx("aspect-[390/435] peer-focus-visible:outline-offset-3 peer-focus-visible:outline-1 peer-focus-visible:outline-solid peer-focus-visible:outline-[#bb6320]", "cursor-pointer group w-full h-full", photo ? "block bg-main-dark/25" : "absolute inset-0 flex flex-col items-center justify-center gap-y-3.5 lg:gap-y-4.75")}>
                    {photo ? (
                      <>
                        <div className="w-full h-full aspect-[390/435]">
                          <img src={URL.createObjectURL(photo)} alt="Preview" className="block w-full h-full object-contain pointer-events-none" />
                        </div>
                        <Cta as="span" className="absolute bottom-3.5 right-3.5 cta-icon-small cta-icon-fill-theme cta-dark pointer-events-none transition-colors duration-200 group-hover:bg-main-dark-800">
                          <Change />
                          <span>Change photo</span>
                        </Cta>
                      </>
                    ) : (
                      <>
                        <div
                          className="flex items-center justify-center gap-[0.625rem] cta cta-icon-stroke-theme cta-dark cta-circle-small lg:cta-circle pointer-events-none group-hover:bg-main-dark-800"
                        >
                          <Plus className="!w-5" />
                        </div>
                        <div className="body-2-sans pointer-events-none">
                          Upload a photo
                        </div>
                      </>
                    )}
                  </label>
                  <input type="file" id="photo" name="photo" accept=".jpg,.jpeg,.png,.webp,.heic" className="peer sr-only" required onChange={onPictureChange} />
                </div>
              </div>
              {errors.photo && (
                <>
                  <span className="block text-form-error-small text-ui-red lg:text-form-error">Image upload failed. Please check the requirements:</span>
                  <ul className="flex flex-col gap-y-1 list-disc pl-4">
                    {errors.photo?.map((error, i) => (
                      <li key={i} className="text-form-error-small text-ui-red lg:text-form-error">{error}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </form>
        </div>
      )}
      {isOverlayOpen && (
        <StoryFormOverlay setIsOpen={setIsOverlayOpen} onAgree={onAgree}/>
      )}
    </div>
  )
}

export default StoryFormLayout;
